import React, { useContext, useEffect, useState } from 'react';
import { Components } from './styled';
import ProjectModal from '../ProjectModal';
import ProjectTable from '../ProjectTable';
import { GridColumns, GridFilterModel, GridSelectionModel } from '@mui/x-data-grid';
import { ProjectTableDataType, ProjectTableType } from '../ProjectTable/types';
import { ProjectTableModalType } from './types';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import { ToastContext } from '../../providers/ToastProvider/context';
import { getDataForSubmission } from './utils';
import { getProjectErrorForCode } from '../../utils/errors';
import { ProjectErrors } from '../../utils/errors/types';
import { isArrayOfStrings } from '../../utils/dataStructures';
import Placeholder from '../Placeholder';
import useTableSearch from '../../hooks/useTableSearch';
import { filteringModel } from '../ProjectTable/constants';
import { getAllLessons, getCalendarLessons } from '../../api/lesson';
import { getAllContentGroups } from '../../api/contentGroup';
import { getEndOfDay } from '../../utils/dateFormat';
import { getAllGoals } from '../../api/contentManagement';

const ModalBody = ({
  children,
  tableData,
  columns,
  selectedRows,
  onSelectionChange,
  filteringModel,
  sortingModel,
}: {
  children?: React.ReactNode;
  tableData: ProjectTableDataType;
  columns: { columns: GridColumns; type: ProjectTableType };
  selectedRows: GridSelectionModel;
  onSelectionChange: (selection: GridSelectionModel) => void;
  filteringModel?: GridFilterModel;
  sortingModel?: { field: string; sort: string };
}) => {
  return (
    <Components.ModalBodyContainer>
      {children}
      {tableData && tableData?.length > 0 ? (
        <ProjectTable
          data={tableData}
          columns={columns}
          selectedRows={selectedRows}
          onSelectionChange={onSelectionChange}
          alternativeBackground
          filteringModel={filteringModel}
          sortingModel={sortingModel}
          checkboxSelection
        />
      ) : (
        <Placeholder type={'big'} />
      )}
    </Components.ModalBodyContainer>
  );
};

const ModalButtons = ({
  onModalClose,
  onContinue,
  isContinueButtonDisabled,
  continueButtonLabel,
  cancelButtonLabel,
}: {
  onModalClose: () => void;
  onContinue: () => void;
  isContinueButtonDisabled?: boolean;
  continueButtonLabel?: string;
  cancelButtonLabel?: string;
}) => {
  return (
    <Components.ModalButtonsContainer>
      <Components.CancelButton onClick={onModalClose}>
        {cancelButtonLabel ? cancelButtonLabel : 'Cancel'}
      </Components.CancelButton>
      <Components.Button onClick={onContinue} isDisabled={isContinueButtonDisabled}>
        {continueButtonLabel ? continueButtonLabel : 'Submit'}
      </Components.Button>
    </Components.ModalButtonsContainer>
  );
};

const ProjectTableModal = ({
  children,
  tableSearchChildren,
  modalConfig,
  isModalOpen,
  modalState,
  initialSelectedRows,
  ignoredIds,
  keepIds,
  onModalClose,
  onModalSubmit,
  modalTitle,
  modalWidth,
  selectOnlyIds,
  continueButtonLabel,
  cancelButtonLabel,
  shouldDisableContinueButton = true,
  singleSelectionOnly = false,
  sortingModel,
  keepSelectedOrder = false,
}: {
  children?: React.ReactNode;
  tableSearchChildren?: React.ReactNode;
  modalConfig: {
    type: ProjectTableModalType;
    tableConfig: { columns: GridColumns; type: ProjectTableType };
    searchLabel?: string;
    category?: string;
    pillarType?: string;
    pillarExpertise?: string;
  };
  isModalOpen: boolean;
  modalState?: any;
  initialSelectedRows?: ProjectTableDataType | string[];
  ignoredIds?: string[];
  keepIds?: string[];
  onModalClose: () => void;
  onModalSubmit: (data: ProjectTableDataType | string[]) => void;
  modalTitle: string;
  modalWidth?: number;
  selectOnlyIds?: boolean;
  continueButtonLabel?: string;
  cancelButtonLabel?: string;
  shouldDisableContinueButton?: boolean;
  singleSelectionOnly?: boolean;
  sortingModel?: { field: string; sort: string };
  keepSelectedOrder?: boolean;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast } = useContext(ToastContext);

  const [tableData, setTableData] = useState<ProjectTableDataType>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { type, tableConfig, searchLabel, category, pillarType, pillarExpertise } = modalConfig;

  const refreshTableData = async () => {
    setIsLoading(true);
    let result;
    const ignoredIdsList = ignoredIds && ignoredIds.length > 0 ? ignoredIds : [];
    const keepIdsList = keepIds && keepIds.length > 0 ? keepIds : [];
    switch (type) {
      case 'selectLessons':
        result = await getAllLessons({ category, pillarType, pillarExpertise });
        if (result && result?.length > 0) {
          const filteredResult = result.filter(
            (item) =>
              (keepIdsList.length > 0 ? keepIdsList.includes(item?.id) : true) && !ignoredIdsList.includes(item?.id),
          );
          setTableData(filteredResult);
        } else {
          setTableData([]);
        }
        break;
      case 'selectCollections':
        const contentGroupType = 'collection';
        result = await getAllContentGroups({ category: category, pillarType, pillarExpertise, contentGroupType });
        if (result && result?.length > 0) {
          const filteredResult = result.filter(
            (item) =>
              (keepIdsList.length > 0 ? keepIdsList.includes(item?.id) : true) && !ignoredIdsList.includes(item?.id),
          );
          setTableData(filteredResult);
        } else {
          setTableData([]);
        }
        break;
      case 'selectBundles':
        const _contentGroupType = 'bundle';
        result = await getAllContentGroups({
          category: category,
          pillarType,
          pillarExpertise,
          contentGroupType: _contentGroupType,
        });
        if (result && result?.length > 0) {
          const filteredResult = result.filter(
            (item) =>
              (keepIdsList.length > 0 ? keepIdsList.includes(item?.id) : true) && !ignoredIdsList.includes(item?.id),
          );
          setTableData(filteredResult);
        } else {
          setTableData([]);
        }
        break;
      case 'showMoreCalendarLessons':
        const startDate = modalState?.startDate;
        const endDate = getEndOfDay(startDate);
        result = endDate ? await getCalendarLessons(startDate, endDate) : [];
        if (result && result?.length > 0) {
          setTableData(result);
        } else {
          setTableData([]);
        }
        break;
      case 'selectGoals':
        result = await getAllGoals();
        if (result && result?.length > 0) {
          const filteredResult = result.filter(
            (item) =>
              (keepIdsList.length > 0 ? keepIdsList.includes(item?.id) : true) && !ignoredIdsList.includes(item?.id),
          );
          setTableData(filteredResult);
        } else {
          setTableData([]);
        }
        break;
    }
    setIsLoading(false);
  };

  useEffect(() => {
    tableSearchProps.setSearchValue(undefined);
    if (isModalOpen) {
      refreshTableData().then();
      if (initialSelectedRows && initialSelectedRows.length > 0) {
        if (isArrayOfStrings(initialSelectedRows)) {
          setSelectedRows(initialSelectedRows as string[]);
        } else {
          const data = (initialSelectedRows as ProjectTableDataType)
            ?.map((item) => ('id' in item ? item?.id : undefined))
            ?.filter((item) => item !== undefined) as string[];
          setSelectedRows(data);
        }
      } else {
        setSelectedRows([]);
      }
    }
  }, [isModalOpen]);

  useEffect(() => {
    refreshTableData().then();
  }, [category, pillarType, pillarExpertise]);

  const onSelectionChange = (selectionModel: GridSelectionModel) => {
    // TODO - see how you can block this to trigger unless actioned by user
    const typedSelectionModel = selectionModel as string[];
    if (singleSelectionOnly) {
      if (selectionModel.length === 0) {
        setSelectedRows([]);
      } else if (selectionModel.length === 1) {
        setSelectedRows(typedSelectionModel);
      } else {
        setSelectedRows([typedSelectionModel?.[1]]);
      }
    } else {
      setSelectedRows(typedSelectionModel);
    }
  };

  const tableSearchProps = useTableSearch();
  const [filter, setFilter] = useState(true);

  const _onModalClose = async () => {
    setSelectedRows([]);
    onModalClose?.();
    setFilter(false);
    tableSearchProps.setSearchValue(undefined);
  };

  const _onSubmit = () => {
    const data = selectOnlyIds ? selectedRows : getDataForSubmission(tableData, selectedRows, keepSelectedOrder);
    if (shouldDisableContinueButton && !(data && data.length > 0)) {
      showErrorToast(getProjectErrorForCode(ProjectErrors.genericTryAgain));
      return;
    }
    onModalSubmit(data);
  };

  return (
    <ProjectModal
      bodyChildren={
        <ModalBody
          tableData={tableData}
          columns={tableConfig}
          selectedRows={selectedRows}
          filteringModel={
            filter
              ? filteringModel([
                  { columnField: 'searchValue', value: tableSearchProps.searchValue, operatorValue: 'contains' },
                ])
              : undefined
          }
          sortingModel={sortingModel}
          onSelectionChange={onSelectionChange}>
          {children}
        </ModalBody>
      }
      buttonChildren={
        <ModalButtons
          onModalClose={_onModalClose}
          onContinue={_onSubmit}
          isContinueButtonDisabled={shouldDisableContinueButton && !(selectedRows && selectedRows.length > 0)}
          continueButtonLabel={continueButtonLabel}
          cancelButtonLabel={cancelButtonLabel}
        />
      }
      tableSearchChildren={tableSearchChildren}
      isModalOpen={isModalOpen}
      onModalClose={_onModalClose}
      modalTitle={modalTitle}
      scrollType={'paper'}
      modalWidth={modalWidth}
      disableCloseOnClickAway
      tableSearchProps={tableSearchProps}
      setFilter={setFilter}
      filter={filter}
      searchLabel={searchLabel}
    />
  );
};

export default ProjectTableModal;
